<script setup>
import {
  onMounted,
  computed,
  watch,
} from 'vue';

import { ThumbUpIcon } from '@thehotelsnetwork/icons/vue/linear';

import { useRouter } from 'vue-router';

import BmOpportunitiesTable from './OpportunitiesTable.vue';

import { percentFormat, numericCurrFormat } from '@/config/valueFormatConfig';
import useOpportunitiesState from '@/state/useOpportunitiesState';
import useSectionState from '@/state/useSectionState';

import Loader from '@/components/Loader/Loader.vue';

import formatValue from '@/utils/formatValue';
import useRequestCache from '@/utils/useRequestCache';

const router = useRouter();

const {
  isUserSummarySection,
} = useSectionState();

const {
  resolved,
  fetchAllOpportunities,
  userOpportunities,
  propertyPerformsOk,
} = useOpportunitiesState();

const { initialized: cacheInitialized } = useRequestCache();

const revenueOpps = computed(() => (userOpportunities.value?.data?.revenue));
const disparitiesOpps = computed(() => (userOpportunities.value?.data?.disparities));

const redirectToSection = () => router.push('/opportunities');

onMounted(() => {
  watch(cacheInitialized, () => {
    if (cacheInitialized.value) {
      fetchAllOpportunities();
    }
  }, { immediate: true });
});

</script>

<template>
  <div
    v-if="resolved"
    class="flex w-full"
  >
    <div
      v-if="revenueOpps?.properties?.length || disparitiesOpps?.properties?.length"
      :class="[
        'revenue-opps flex w-full sm:flex-wrap',
        { 'xl:flex-nowrap xl:gap-8 ': isUserSummarySection },
      ]"
      v-on="isUserSummarySection && { click: () => redirectToSection() }"
    >
      <LpCard
        v-if="revenueOpps?.properties?.length"
        :class="[
          'mt-6 w-full overflow-hidden',
          { 'card-highlight flex flex-col justify-between max-w1/2': isUserSummarySection },
        ]"
      >
        <LpCollapsible
          class="collapsible"
          :ui="{
            header: '.collapsible-header',
            headerTitle: '.collapsible-header-title',
          }"
        >
          <template #title>
            {{ $t('Increase your revenue in {count} of your properties', { count: revenueOpps.properties.length }) }}
          </template>
          <template #content>
            <BmOpportunitiesTable :table-rows="revenueOpps.properties" />
          </template>
        </LpCollapsible>
        <div class="flex p-6 opportunityCard__footer bg-base-100">
          <div v-if="!isUserSummarySection">
            <span class="opportunityCard__footer--name"> {{ $t('Goal') }} </span>
            <span class="opportunityCard__footer--value"> {{ $t('Revenue') }} </span>
          </div>
          <div v-if="revenueOpps.uplift_total">
            <span class="opportunityCard__footer--name"> {{ $t('Estimated Uplift') }} </span>
            <span class="opportunityCard__footer--value">
              +{{ formatValue(revenueOpps.uplift_total, percentFormat) }}
            </span>
          </div>
          <div v-if="revenueOpps.lost_revenue_total">
            <span class="opportunityCard__footer--name"> {{ $t('Total Lost Revenue') }} </span>
            <div>
              <LpTag
                color="primary"
                size="medium"
              >
                <span class="opportunityCard__footer--value">
                  {{ formatValue(revenueOpps.lost_revenue_total, numericCurrFormat) }}
                </span>
              </LpTag>
            </div>
          </div>
        </div>
      </LpCard>
      <LpCard
        v-if="disparitiesOpps?.properties?.length"
        :class="[
          'disparities-opps mt-6 w-full overflow-hidden',
          { 'card-highlight flex flex-col justify-between': isUserSummarySection },
        ]"
      >
        <LpCollapsible
          class="collapsible"
          :ui="{
            header: '.collapsible-header',
            headerTitle: '.collapsible-header-title',
          }"
        >
          <template #title>
            {{ $t('Decrease your disparities in {count} of your properties', {
              count: disparitiesOpps.properties.length,
            }) }}
          </template>
          <template #content>
            <BmOpportunitiesTable
              :table-rows="disparitiesOpps.properties"
              is-disparities
            />
          </template>
        </LpCollapsible>
        <div class="flex p-6 opportunityCard__footer bg-base-100">
          <div v-if="!isUserSummarySection">
            <span class="opportunityCard__footer--name"> {{ $t('Goal') }} </span>
            <span class="opportunityCard__footer--value"> {{ $t('Disparities') }} </span>
          </div>
          <div v-if="disparitiesOpps.uplift_total">
            <span class="opportunityCard__footer--name"> {{ $t('Estimated drop') }} </span>
            <span class="opportunityCard__footer--value">
              -{{ formatValue(disparitiesOpps.uplift_total, percentFormat) }}
            </span>
          </div>
          <div v-if="disparitiesOpps.lost_revenue_total">
            <span class="opportunityCard__footer--name"> {{ $t('Total disparities to combat') }} </span>
            <div>
              <LpTag
                color="primary"
                size="medium"
              >
                <span class="opportunityCard__footer--value">
                  {{ disparitiesOpps.lost_revenue_total }}
                </span>
              </LpTag>
            </div>
          </div>
        </div>
      </LpCard>
    </div>
    <!-- Property OK -->
    <LpCard
      v-else-if="propertyPerformsOk"
      :class="[
        'opportunitiesList__userMessage',
        { 'opportunitiesList__userMessage--summary': isUserSummarySection },
        { 'opportunitiesList__userMessage--section': !isUserSummarySection },
      ]"
    >
      <ThumbUpIcon class="w-8 h-8 text-primary-500" />
      <h1 class="mt-6 text-2xl font-bold section-sub-title">
        {{ $t('You are doing great!') }}
      </h1>
      <p class="mt-2 text-xl font-medium section-body text-medium">
        {{ $t('No opportunities have been found, you are overperforming your Proximity compset. Keep up the good work!')
        }}
      </p>
    </LpCard>
    <!-- No Data -->
    <LpCard
      v-else
      :class="[
        'opportunitiesList__userMessage',
        { 'opportunitiesList__userMessage--summary': isUserSummarySection },
        { 'opportunitiesList__userMessage--section': !isUserSummarySection },
      ]"
    >
      <img
        src="@/assets/img/icons/common/no-matches-found-icon.svg"
        alt=""
        class="w-12 h-12"
      >
      <h1 class="mt-6 text-2xl font-bold section-sub-title">
        {{ $t('Sorry! There is not enough data available to identify opportunities') }}
      </h1>
      <p class="mt-2 text-xl font-medium section-body text-medium">
        {{ $t('Feel free to come back later!') }}
      </p>
    </LpCard>
  </div>
  <loader
    v-else
    class="mx-auto mt-12"
    size="big"
  />
</template>

<style scoped lang="postcss">
.collapsible :deep(.collapsible-header) {
  @apply p-6;
}
.collapsible :deep(.collapsible-header-title) {
  @apply flex items-center flex-1 text-base-900 text-xl font-medium;
}

.opportunitiesList__userMessage {
  @apply m-auto mt-10 mb-2 flex flex-col justify-center items-center max-w-xl text-center;
  &--summary {
    @apply m-0 mt-6 p-8 max-w-full flex-1 bg-base-100;
    img {
      @apply w-24;
    }
    .section-sub-title {
      @apply text-lg leading-3;
    }
    .section-body {
      @apply text-base;
    }
  }
  &--section {
    @apply shadow-none;
  }
}
.opportunityCard__footer {
  > * {
    @apply flex flex-col mr-10 gap-2;
  }
  &--name {
    @apply text-base-700 text-sm;
  }
  &--value {
    @apply text-base-900 text-lg py-0.5;

  }
}
</style>
